import React, { useState, useEffect, useCallback } from "react";
import Base from "../core/Base";
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, Alert,
  Stack, Pagination, FormControl, InputLabel, Select, MenuItem,
  Tooltip, Skeleton, Tabs, Tab, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Switch, FormControlLabel, Divider,
} from "@mui/material";
import DeleteSweepIcon      from "@mui/icons-material/DeleteSweep";
import ComputerIcon         from "@mui/icons-material/Computer";
import SmartphoneIcon       from "@mui/icons-material/Smartphone";
import TabletIcon           from "@mui/icons-material/Tablet";
import DeviceUnknownIcon    from "@mui/icons-material/DeviceUnknown";
import BlockIcon            from "@mui/icons-material/Block";
import LockOpenIcon         from "@mui/icons-material/LockOpen";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { isAutheticated }   from "../auth/helper";
import {
  getLoginLogs, clearLoginLogs, clearOldLogs, getAllSubAdmins,
  getBlockedIps, blockIp, unblockIp, updatePagePerms,
} from "./helper/adminapicall";

const deviceIcon = (d) => {
  if (d === "Mobile")  return <SmartphoneIcon sx={{ fontSize: 15 }} />;
  if (d === "Tablet")  return <TabletIcon     sx={{ fontSize: 15 }} />;
  if (d === "Desktop") return <ComputerIcon   sx={{ fontSize: 15 }} />;
  return <DeviceUnknownIcon sx={{ fontSize: 15 }} />;
};

const roleChip = (role) => (
  <Chip label={role === 1 ? "Super Admin" : "Subadmin"} size="small"
    color={role === 1 ? "warning" : "default"}
    sx={{ fontWeight: 700, fontSize: "0.67rem", height: 18 }} />
);
const eventChip = (ev) => (
  <Chip label={ev === "logout" ? "Logout" : "Login"} size="small"
    color={ev === "logout" ? "error" : "success"} variant="outlined"
    sx={{ fontWeight: 700, fontSize: "0.67rem", height: 18 }} />
);

const BlockedIpsTab = ({ admin, token }) => {
  const isSuperAdmin = admin.role === 1;
  const canBlock     = isSuperAdmin || admin.allowedPages?.includes("ip-blocking");

  const [blockedList, setBlockedList] = useState([]);
  const [subadmins,   setSubadmins]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [msg,         setMsg]         = useState({ text: "", severity: "success" });
  const [dialog,      setDialog]      = useState(false);
  const [inputIp,     setInputIp]     = useState("");
  const [inputReason, setInputReason] = useState("");
  const [saving,      setSaving]      = useState(false);

  const flash = (text, severity = "success") => { setMsg({ text, severity }); setTimeout(() => setMsg({ text: "" }), 4000); };

  const load = useCallback(() => {
    if (!canBlock) return;
    setLoading(true);
    getBlockedIps(admin._id, token)
      .then((d) => setBlockedList(Array.isArray(d) ? d : []))
      .catch(() => flash("Failed to load blocked IPs", "error"))
      .finally(() => setLoading(false));
  }, [admin._id, token, canBlock]);

  useEffect(() => {
    load();
    if (isSuperAdmin) getAllSubAdmins(admin._id, token).then((d) => setSubadmins(Array.isArray(d) ? d : []));
  }, []); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => { setInputIp(e.detail || ""); setInputReason(""); setDialog(true); };
    window.addEventListener("blockIpRequest", handler);
    return () => window.removeEventListener("blockIpRequest", handler);
  }, []);

  const handleBlock = async () => {
    if (!inputIp.trim()) return;
    setSaving(true);
    try {
      const res = await blockIp(admin._id, token, { ip: inputIp.trim(), reason: inputReason.trim() });
      if (res.error) { flash(res.error, "error"); }
      else { flash(`${inputIp.trim()} blocked`); setDialog(false); setInputIp(""); setInputReason(""); load(); }
    } catch { flash("Failed to block IP", "error"); }
    finally { setSaving(false); }
  };

  const handleUnblock = async (id, ip) => {
    if (!window.confirm(`Unblock ${ip}?`)) return;
    try { await unblockIp(admin._id, token, id); flash(`${ip} unblocked`); setBlockedList((prev) => prev.filter((b) => b._id !== id)); }
    catch { flash("Failed to unblock IP", "error"); }
  };

  const handlePermToggle = async (subadmin) => {
    const pages   = subadmin.allowedPages || [];
    const hasPerms = pages.includes("ip-blocking");
    const updated  = hasPerms ? pages.filter((p) => p !== "ip-blocking") : [...pages, "ip-blocking"];
    try {
      await updatePagePerms(admin._id, token, subadmin._id, { allowedPages: updated });
      setSubadmins((prev) => prev.map((s) => s._id === subadmin._id ? { ...s, allowedPages: updated } : s));
      flash(`${hasPerms ? "Revoked" : "Granted"} IP blocking for ${subadmin.name}`);
    } catch { flash("Failed to update permission", "error"); }
  };

  if (!canBlock) return <Alert severity="warning" sx={{ mt: 2 }}>You do not have permission to manage blocked IPs.</Alert>;

  return (
    <Box>
      {msg.text && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg({ text: "" })}>{msg.text}</Alert>}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Blocked IPs</Typography>
          <Typography variant="body2" color="text.secondary">
            {blockedList.length} IP{blockedList.length !== 1 ? "s" : ""} blocked · logins from these IPs are rejected
          </Typography>
        </Box>
        <Button variant="contained" color="error" size="small" startIcon={<AddCircleOutlineIcon />}
          onClick={() => { setInputIp(""); setInputReason(""); setDialog(true); }}>
          Block IP
        </Button>
      </Box>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, mb: 3 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["IP Address", "Reason", "Blocked By", "Blocked At", "Actions"].map((h) => <TableCell key={h}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 5 }).map((__, j) => <TableCell key={j}><Skeleton height={20} /></TableCell>)}</TableRow>
                ))
              ) : blockedList.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5, color: "text.secondary" }}>No IPs are currently blocked</TableCell></TableRow>
              ) : blockedList.map((b) => (
                <TableRow key={b._id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <BlockIcon sx={{ fontSize: 14, color: "error.main" }} />
                      <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.82rem", color: "error.main", fontWeight: 700 }}>{b.ip}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: "0.78rem", maxWidth: 240 }}>{b.reason || <em>—</em>}</TableCell>
                  <TableCell sx={{ fontSize: "0.78rem" }}>{b.blockedByName || "—"}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.72rem" }}>
                      {b.createdAt ? new Date(b.createdAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Unblock">
                      <IconButton size="small" color="success" onClick={() => handleUnblock(b._id, b.ip)}>
                        <LockOpenIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {isSuperAdmin && subadmins.length > 0 && (
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>Delegate IP Blocking</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Toggle which subadmins can block/unblock IPs.</Typography>
          <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Subadmin", "Email", "IP Blocking Permission"].map((h) => <TableCell key={h}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {subadmins.map((s) => (
                  <TableRow key={s._id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: "0.78rem" }}>{s.email}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={<Switch size="small" checked={s.allowedPages?.includes("ip-blocking") || false} onChange={() => handlePermToggle(s)} color="error" />}
                        label={<Typography variant="caption" fontWeight={600}>{s.allowedPages?.includes("ip-blocking") ? "Granted" : "Not granted"}</Typography>}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Block IP Address</DialogTitle>
        <DialogContent>
          <TextField label="IP Address" value={inputIp} onChange={(e) => setInputIp(e.target.value)}
            fullWidth size="small" sx={{ mt: 1, mb: 2 }} placeholder="e.g. 203.0.113.42" autoFocus inputProps={{ spellCheck: false }} />
          <TextField label="Reason (optional)" value={inputReason} onChange={(e) => setInputReason(e.target.value)}
            fullWidth size="small" multiline rows={2} placeholder="Why is this IP being blocked?" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleBlock} disabled={saving || !inputIp.trim()} startIcon={<BlockIcon />}>
            {saving ? "Blocking…" : "Block IP"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const LoginLogs = () => {
  const { admin, token } = isAutheticated();
  const canBlock = admin.role === 1 || admin.allowedPages?.includes("ip-blocking");

  const [tab,     setTab]     = useState(0);
  const [data,    setData]    = useState({ logs: [], total: 0, pages: 1 });
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(true);
  const [admins,  setAdmins]  = useState([]);
  const [filter,  setFilter]  = useState("");
  const [msg,     setMsg]     = useState({ text: "", severity: "success" });

  const flash = (text, severity = "success") => { setMsg({ text, severity }); setTimeout(() => setMsg({ text: "" }), 4000); };

  const load = useCallback((p = 1, f = filter) => {
    setLoading(true);
    getLoginLogs(admin._id, token, { page: p, filterAdminId: f })
      .then((d) => { if (d && d.logs) setData(d); })
      .catch(() => flash("Failed to load logs", "error"))
      .finally(() => setLoading(false));
  }, [admin._id, token, filter]);

  useEffect(() => {
    load(1);
    getAllSubAdmins(admin._id, token).then((d) => setAdmins(Array.isArray(d) ? d : []));
  }, []); // eslint-disable-line

  const handleFilter = (val) => { setFilter(val); setPage(1); load(1, val); };
  const handleClear  = async () => {
    if (!window.confirm("Delete ALL login logs? This cannot be undone.")) return;
    try { const r = await clearLoginLogs(admin._id, token); flash(`Cleared ${r?.deletedCount ?? "all"} logs`); setData({ logs: [], total: 0, pages: 1 }); setPage(1); }
    catch { flash("Clear failed", "error"); }
  };
  const handleClearOld = async () => {
    if (!window.confirm("Delete logs older than 1 year?")) return;
    try { const r = await clearOldLogs(admin._id, token); flash(`Removed ${r?.deletedCount ?? 0} old log(s)`); load(1); }
    catch { flash("Clear failed", "error"); }
  };
  const handleBlockFromLog = (ip) => {
    setTab(1);
    setTimeout(() => window.dispatchEvent(new CustomEvent("blockIpRequest", { detail: ip })), 150);
  };

  const { logs, total, pages } = data;

  return (
    <Base>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Login Logs</Typography>
          <Typography variant="body2" color="text.secondary">Admin sign-ins &amp; logouts · 1 year retention · {total} records</Typography>
        </Box>
        {tab === 0 && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" color="warning" size="small" startIcon={<DeleteSweepIcon />} onClick={handleClearOld}>Clear &gt;1 Year</Button>
            <Button variant="outlined" color="error"   size="small" startIcon={<DeleteSweepIcon />} onClick={handleClear}>Clear All</Button>
          </Box>
        )}
      </Box>

      {msg.text && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg({ text: "" })}>{msg.text}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Tab label="Login Logs" />
        {canBlock && <Tab label="Blocked IPs" icon={<BlockIcon sx={{ fontSize: 16 }} />} iconPosition="start" />}
      </Tabs>

      {tab === 0 && (
        <>
          <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 3, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by admin</InputLabel>
              <Select label="Filter by admin" value={filter} onChange={(e) => handleFilter(e.target.value)}>
                <MenuItem value="">All admins</MenuItem>
                <MenuItem value={admin._id}>Me ({admin.name})</MenuItem>
                {admins.map((a) => <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Paper>

          <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Event", "Admin", "Role", "IP Address", "Device", "OS", "Browser", "Time"].map((h) => <TableCell key={h}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <TableRow key={i}>{Array.from({ length: 8 }).map((__, j) => <TableCell key={j}><Skeleton height={20} /></TableCell>)}</TableRow>
                    ))
                  ) : logs.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5, color: "text.secondary" }}>No login logs found</TableCell></TableRow>
                  ) : logs.map((log, i) => (
                    <TableRow key={log._id || i} hover>
                      <TableCell>{eventChip(log.event)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{log.adminName}</TableCell>
                      <TableCell>{roleChip(log.adminRole)}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.78rem", color: "primary.light" }}>{log.ip || "—"}</Typography>
                          {canBlock && log.ip && log.ip !== "unknown" && (
                            <Tooltip title={`Block ${log.ip}`}>
                              <IconButton size="small" color="error" sx={{ p: 0.2 }} onClick={() => handleBlockFromLog(log.ip)}>
                                <BlockIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {deviceIcon(log.device)}
                          <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>{log.device || "—"}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: "0.75rem" }}>{log.os || "—"}</TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: "0.75rem" }}>{log.browser || "—"}</TableCell>
                      <TableCell>
                        <Tooltip title={log.userAgent || ""} placement="top">
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.72rem", cursor: "default" }}>
                            {log.createdAt ? new Date(log.createdAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {pages > 1 && (
              <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid", borderColor: "divider", display: "flex", justifyContent: "center" }}>
                <Pagination count={pages} page={page} onChange={(_, p) => { setPage(p); load(p); }} size="small" color="primary" siblingCount={1} />
              </Box>
            )}
          </Paper>
        </>
      )}

      {tab === 1 && canBlock && <BlockedIpsTab admin={admin} token={token} />}
    </Base>
  );
};

export default LoginLogs;
